/** @format */

import { useEffect, useState, useRef, useCallback } from "react";
import Button from "../extra/Button";
import Input from "../extra/Input";
import { useNavigate } from "react-router-dom";
import { loginAdmin, loginStep1, loginVerifyOtp, resendOtp } from "../store/admin/admin.action";
import { connect, useSelector } from "react-redux";
import Declotr from "../../assets/images/DeclotrImage.png";
import loginlogo2 from "../../assets/images/loginlogo2.png";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

const Login = (props) => {
	const navigate = useNavigate();

	// Step state: "credentials" or "otp"
	const [step, setStep] = useState("credentials");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
	const [loading, setLoading] = useState(false);
	const [resendTimer, setResendTimer] = useState(0);
	const inputRefs = useRef([]);

	const [error, setError] = useState({
		email: "",
		password: "",
	});

	// Resend cooldown timer
	useEffect(() => {
		if (resendTimer <= 0) return;
		const interval = setInterval(() => {
			setResendTimer((prev) => prev - 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [resendTimer]);

	// ─── Step 1: Submit email + password ───
	const handleCredentialSubmit = () => {
		if (!email || !password) {
			let err = {};
			if (!email) err.email = "Email Is Required !";
			if (!password) err.password = "Password is required !";
			return setError({ ...err });
		}

		setLoading(true);
		props.loginStep1({ email, password }, (returnedEmail) => {
			setLoading(false);
			setStep("otp");
			setResendTimer(RESEND_COOLDOWN);
			// Focus first OTP input after transition
			setTimeout(() => inputRefs.current[0]?.focus(), 100);
		});

		// If loginStep1 fails (toast shown), reset loading after a delay
		setTimeout(() => setLoading(false), 5000);
	};

	// ─── Step 2: Verify OTP ───
	const handleOtpSubmit = useCallback(() => {
		const otp = otpDigits.join("");
		if (otp.length !== OTP_LENGTH) return;

		setLoading(true);
		props.loginVerifyOtp({ email, otp }, navigate);
		setTimeout(() => setLoading(false), 5000);
	}, [otpDigits, email, navigate, props]);

	// ─── OTP input handlers ───
	const handleOtpChange = (index, value) => {
		// Only allow digits
		const digit = value.replace(/\D/g, "").slice(-1);
		const newDigits = [...otpDigits];
		newDigits[index] = digit;
		setOtpDigits(newDigits);

		// Auto-advance to next input
		if (digit && index < OTP_LENGTH - 1) {
			inputRefs.current[index + 1]?.focus();
		}

		// Auto-submit when all digits filled
		if (digit && index === OTP_LENGTH - 1) {
			const fullOtp = newDigits.join("");
			if (fullOtp.length === OTP_LENGTH) {
				setTimeout(() => {
					setLoading(true);
					props.loginVerifyOtp({ email, otp: fullOtp }, navigate);
					setTimeout(() => setLoading(false), 5000);
				}, 150);
			}
		}
	};

	const handleOtpKeyDown = (index, e) => {
		if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
		if (e.key === "Enter") {
			handleOtpSubmit();
		}
	};

	const handleOtpPaste = (e) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
		if (!pasted) return;
		const newDigits = [...otpDigits];
		for (let i = 0; i < pasted.length; i++) {
			newDigits[i] = pasted[i];
		}
		setOtpDigits(newDigits);
		// Focus last filled or next empty
		const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
		inputRefs.current[focusIdx]?.focus();

		// Auto-submit if all filled
		if (pasted.length === OTP_LENGTH) {
			setTimeout(() => {
				setLoading(true);
				props.loginVerifyOtp({ email, otp: pasted }, navigate);
				setTimeout(() => setLoading(false), 5000);
			}, 150);
		}
	};

	const handleResend = () => {
		if (resendTimer > 0) return;
		props.resendOtp({ email });
		setResendTimer(RESEND_COOLDOWN);
	};

	const handleBackToCredentials = () => {
		setStep("credentials");
		setOtpDigits(Array(OTP_LENGTH).fill(""));
		setLoading(false);
	};

	// Mask email for display
	const maskedEmail = email ? email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + "*".repeat(b.length) + c) : "";

	return (
		<>
			<div className='mainLoginPage'>
				<div className='loginDiv'>
					<div className='row'>
						<div className='col-xl-6 d-xxl-block d-xl-block d-none boxCenter p-5' style={{ background: "linear-gradient(135deg, #e54a13 0%, #c13a0e 100%)" }}>
							<img className='img-fluid' src={Declotr} alt='text logo' style={{ maxWidth: "200px", marginBottom: "20px" }} />
							<img className='img-fluid' src={loginlogo2} alt='login logo' />
						</div>
						<div className='col-xl-6 col-md-12 boxCenter' style={{ background: "#ffffff" }}>
							<div className='loginDiv2'>
								<div className='loginPage pt-3'>
									<div className='my-4'>
										<div className='loginLogo me-3 pt-1 pe-1'>
											<img src={require("../../assets/images/Eralogo22.png")} alt='' width={"60px"} />
										</div>
									</div>

									{step === "credentials" ? (
										<>
											{/* ─── STEP 1: Email + Password ─── */}
											<div style={{ color: "#64748b", fontSize: "14px", fontWeight: "400", lineHeight: "20px", marginBottom: "4px" }}>
												<p>Welcome back</p>
											</div>
											<div className='mb-4'>
												<h3 style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a", letterSpacing: "-0.02em" }}>Sign in to your account</h3>
											</div>
											<div className='loginInput'>
												<Input
													label={`Email`}
													id={`loginEmail`}
													type={`email`}
													value={email}
													errorMessage={error.email && error.email}
													onChange={(e) => {
														setEmail(e.target.value);
														if (!e.target.value) {
															return setError({ ...error, email: `Email Is Required` });
														} else {
															return setError({ ...error, email: "" });
														}
													}}
													onKeyPress={(e) => e.key === "Enter" && handleCredentialSubmit()}
												/>

												<Input
													label={`Password`}
													id={`loginPassword`}
													type={`password`}
													value={password}
													errorMessage={error.password && error.password}
													onChange={(e) => {
														setPassword(e.target.value);
														if (!e.target.value) {
															return setError({ ...error, password: `Password Is Required` });
														} else {
															return setError({ ...error, password: "" });
														}
													}}
													onKeyPress={(e) => e.key === "Enter" && handleCredentialSubmit()}
												/>
											</div>

											<div className='d-flex align-items-center justify-content-end mb-2'>
												<a href='/forgotPassword' style={{ color: "#e54a13", fontSize: "13px", fontWeight: "500", textDecoration: "none" }}>
													Forgot password?
												</a>
											</div>

											<div className='loginButton mt-4'>
												<Button
													newClass={`blackFont w-100`}
													btnColor={`btnBlackPrime`}
													style={{
														borderRadius: "10px",
														width: "100%",
														height: "48px",
														color: "#fff",
														fontSize: "15px",
														fontWeight: "600",
														opacity: loading ? 0.7 : 1,
														pointerEvents: loading ? "none" : "auto",
													}}
													btnName={loading ? "Verifying..." : "Continue"}
													onClick={handleCredentialSubmit}
												/>
											</div>
										</>
									) : (
										<>
											{/* ─── STEP 2: OTP Verification ─── */}
											<div className='mb-2'>
												<button
													onClick={handleBackToCredentials}
													style={{
														background: "none",
														border: "none",
														color: "#64748b",
														fontSize: "13px",
														cursor: "pointer",
														padding: 0,
														display: "flex",
														alignItems: "center",
														gap: "4px",
													}}>
													← Back
												</button>
											</div>
											<div className='mb-2'>
												<h3 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", letterSpacing: "-0.02em" }}>Check your email</h3>
											</div>
											<div style={{ color: "#64748b", fontSize: "14px", lineHeight: "22px", marginBottom: "28px" }}>
												<p style={{ margin: 0 }}>
													We sent a 6-digit code to <strong style={{ color: "#0f172a" }}>{maskedEmail}</strong>
												</p>
											</div>

											{/* OTP Input Boxes */}
											<div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "28px" }}>
												{otpDigits.map((digit, idx) => (
													<input
														key={idx}
														ref={(el) => (inputRefs.current[idx] = el)}
														type='text'
														inputMode='numeric'
														maxLength={1}
														value={digit}
														onChange={(e) => handleOtpChange(idx, e.target.value)}
														onKeyDown={(e) => handleOtpKeyDown(idx, e)}
														onPaste={idx === 0 ? handleOtpPaste : undefined}
														style={{
															width: "50px",
															height: "56px",
															textAlign: "center",
															fontSize: "22px",
															fontWeight: "700",
															color: "#0f172a",
															border: digit ? "2px solid #e54a13" : "2px solid #e2e8f0",
															borderRadius: "10px",
															outline: "none",
															transition: "border-color 0.2s",
															background: "#f8fafc",
														}}
														onFocus={(e) => (e.target.style.borderColor = "#e54a13")}
														onBlur={(e) => (e.target.style.borderColor = digit ? "#e54a13" : "#e2e8f0")}
													/>
												))}
											</div>

											<div className='loginButton'>
												<Button
													newClass={`blackFont w-100`}
													btnColor={`btnBlackPrime`}
													style={{
														borderRadius: "10px",
														width: "100%",
														height: "48px",
														color: "#fff",
														fontSize: "15px",
														fontWeight: "600",
														opacity: loading || otpDigits.join("").length !== OTP_LENGTH ? 0.7 : 1,
														pointerEvents: loading ? "none" : "auto",
													}}
													btnName={loading ? "Verifying..." : "Verify & Sign In"}
													onClick={handleOtpSubmit}
												/>
											</div>

											<div className='text-center mt-4'>
												<span style={{ color: "#64748b", fontSize: "13px" }}>
													Didn't receive the code?{" "}
													{resendTimer > 0 ? (
														<span style={{ color: "#94a3b8" }}>Resend in {resendTimer}s</span>
													) : (
														<button
															onClick={handleResend}
															style={{
																background: "none",
																border: "none",
																color: "#e54a13",
																fontSize: "13px",
																fontWeight: "600",
																cursor: "pointer",
																padding: 0,
																textDecoration: "underline",
															}}>
															Resend Code
														</button>
													)}
												</span>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default connect(null, { loginAdmin, loginStep1, loginVerifyOtp, resendOtp })(Login);
